import { runningFiniteAnimations } from "./scroll-box-animations"
import type { ScrollBoxFinding } from "./scroll-box-findings"
import { descendantsNearView } from "./scroll-box-near-view"
import { claimScrollBoxReport } from "./scroll-box-report-ledger"
import { isRendered } from "./scroll-box-styles"
import { detectElementFindings, detectPageFindings, isInspectedElement } from "./scroll-box-sweep"

export const SCROLL_BOX_SETTLE_MS = 500

const MAXIMUM_REPORTS_PER_PAGE_LOAD = 5
const SIZE_BUCKET_PX = 50
const DIRTY_ROOT_FLUSH_MAXIMUM_WAIT_MS = 5000
const OBSERVED_ATTRIBUTES = ["class", "style", "hidden", "open", "aria-busy", "aria-expanded"]
const BUSY_REGION_SELECTOR = '[aria-busy="true"]'
const REPORT_KEY_SEPARATOR = "|"

export interface SupportedMinimumWindow {
  widthPx: number
  heightPx: number
}

export interface ScrollBoxReport {
  finding: ScrollBoxFinding
  route: string
  windowWidthPx: number
  windowHeightPx: number
  devicePixelRatio: number
  checksSkippedBelowMinimumWindow: number
}

export interface ScrollBoxWatcherOptions {
  minimumWholeUnits: number
  settleMs: number
  supportedMinimumWindow: SupportedMinimumWindow
  reportStore: Storage
  currentRoute: () => string
  report: (report: ScrollBoxReport) => void
}

type FindingScope = Element | Document

function sizeBucket(widthPx: number, heightPx: number): string {
  const bucketed = (sizePx: number) => Math.round(sizePx / SIZE_BUCKET_PX) * SIZE_BUCKET_PX
  return `${bucketed(widthPx)}x${bucketed(heightPx)}`
}

function findingKey(finding: ScrollBoxFinding): string {
  return [finding.kind, finding.identity.key].join(REPORT_KEY_SEPARATOR)
}

function inspectedElementsWithin(root: Element): Element[] {
  return [root, ...descendantsNearView(root)].filter(isInspectedElement)
}

function enclosingInspectedElements(element: Element): Element[] {
  const enclosing: Element[] = []
  for (let ancestor = element.parentElement; ancestor && ancestor !== document.body; ancestor = ancestor.parentElement) {
    if (isInspectedElement(ancestor)) enclosing.push(ancestor)
  }
  return enclosing
}

function outermostConnectedRoots(roots: Element[]): Element[] {
  const connectedRoots = roots.filter((root) => root.isConnected)
  return connectedRoots.filter((root) => !connectedRoots.some((other) => other !== root && other.contains(root)))
}

function isInsideBusyRegion(element: Element): boolean {
  return element.closest(BUSY_REGION_SELECTOR) !== null
}

class ScrollBoxWatcher {
  private readonly observedElements = new Set<Element>()
  private readonly settleTimers = new Map<FindingScope, number>()
  private readonly knownFindingKeys = new Map<FindingScope, Set<string>>()
  private readonly dirtyRoots = new Set<Element>()
  private dirtyRootFlushTimer: number | null = null
  private firstDirtyAtMs: number | null = null
  private reportsThisPageLoad = 0
  private checksSkippedBelowMinimumWindow = 0
  private stopped = false
  private readonly resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) this.scheduleCheck(entry.target)
  })
  private readonly mutationObserver = new MutationObserver((records) => this.recordMutations(records))
  private readonly recheckEverything = () => {
    for (const element of this.observedElements) this.scheduleCheck(element)
    this.scheduleCheck(document)
  }

  constructor(private readonly options: ScrollBoxWatcherOptions) {}

  start(): void {
    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: OBSERVED_ATTRIBUTES,
    })
    window.addEventListener("resize", this.recheckEverything)
    window.visualViewport?.addEventListener("resize", this.recheckEverything)
    void document.fonts.ready.then(() => this.markDirty(document.body))
  }

  stop(): void {
    this.stopped = true
    this.mutationObserver.disconnect()
    this.resizeObserver.disconnect()
    window.removeEventListener("resize", this.recheckEverything)
    window.visualViewport?.removeEventListener("resize", this.recheckEverything)
    for (const timer of this.settleTimers.values()) window.clearTimeout(timer)
    this.settleTimers.clear()
    if (this.dirtyRootFlushTimer !== null) window.clearTimeout(this.dirtyRootFlushTimer)
  }

  private recordMutations(records: MutationRecord[]): void {
    for (const record of records) {
      if (record.target instanceof Element) this.markDirty(record.target)
    }
  }

  private markDirty(root: Element): void {
    if (this.stopped) return
    this.dirtyRoots.add(root)
    const nowMs = performance.now()
    this.firstDirtyAtMs ??= nowMs
    const remainingMaximumWaitMs = DIRTY_ROOT_FLUSH_MAXIMUM_WAIT_MS - (nowMs - this.firstDirtyAtMs)
    if (this.dirtyRootFlushTimer !== null) window.clearTimeout(this.dirtyRootFlushTimer)
    this.dirtyRootFlushTimer = window.setTimeout(
      () => this.flushDirtyRoots(),
      Math.max(0, Math.min(this.options.settleMs, remainingMaximumWaitMs)),
    )
  }

  private flushDirtyRoots(): void {
    const roots = outermostConnectedRoots([...this.dirtyRoots])
    this.dirtyRoots.clear()
    this.dirtyRootFlushTimer = null
    this.firstDirtyAtMs = null
    for (const element of this.observedElements) {
      if (!element.isConnected) this.forgetElement(element)
    }
    for (const root of roots) {
      for (const element of [...enclosingInspectedElements(root), ...inspectedElementsWithin(root)]) {
        this.watchElement(element)
      }
    }
    this.scheduleCheck(document)
  }

  private watchElement(element: Element): void {
    if (this.observedElements.has(element)) {
      this.scheduleCheck(element)
      return
    }
    this.observedElements.add(element)
    this.resizeObserver.observe(element)
  }

  private forgetElement(element: Element): void {
    this.resizeObserver.unobserve(element)
    this.observedElements.delete(element)
    this.knownFindingKeys.delete(element)
    const pendingTimer = this.settleTimers.get(element)
    if (pendingTimer !== undefined) window.clearTimeout(pendingTimer)
    this.settleTimers.delete(element)
  }

  private scheduleCheck(scope: FindingScope): void {
    if (this.stopped) return
    const pendingTimer = this.settleTimers.get(scope)
    if (pendingTimer !== undefined) window.clearTimeout(pendingTimer)
    this.settleTimers.set(
      scope,
      window.setTimeout(() => this.checkSettledScope(scope), this.options.settleMs),
    )
  }

  private isBelowSupportedMinimumWindow(): boolean {
    const minimum = this.options.supportedMinimumWindow
    return window.innerWidth < minimum.widthPx || window.innerHeight < minimum.heightPx
  }

  private isAnimating(scope: FindingScope): boolean {
    return runningFiniteAnimations(scope).length > 0
  }

  private checkSettledScope(scope: FindingScope): void {
    this.settleTimers.delete(scope)
    if (scope instanceof Element && !scope.isConnected) {
      this.forgetElement(scope)
    } else if (this.isBelowSupportedMinimumWindow()) {
      this.checksSkippedBelowMinimumWindow++
    } else if (this.isAnimating(scope)) {
      this.scheduleCheck(scope)
    } else if (scope instanceof Document) {
      this.recordFindings(scope, this.pageFindingsOutsideBusyRegions())
    } else if (isRendered(scope) && !isInsideBusyRegion(scope)) {
      this.recordFindings(scope, detectElementFindings(scope, this.options.minimumWholeUnits))
    }
  }

  private pageFindingsOutsideBusyRegions(): ScrollBoxFinding[] {
    return detectPageFindings()
      .filter((detected) => !isInsideBusyRegion(detected.element))
      .map((detected) => detected.finding)
  }

  private recordFindings(scope: FindingScope, findings: ScrollBoxFinding[]): void {
    const previouslyKnown = this.knownFindingKeys.get(scope) ?? new Set<string>()
    this.knownFindingKeys.set(scope, new Set(findings.map(findingKey)))
    for (const finding of findings) {
      if (!previouslyKnown.has(findingKey(finding))) this.reportFinding(finding)
    }
  }

  private reportFinding(finding: ScrollBoxFinding): void {
    if (this.reportsThisPageLoad >= MAXIMUM_REPORTS_PER_PAGE_LOAD) return
    const route = this.options.currentRoute()
    const reportKey = [
      route,
      findingKey(finding),
      sizeBucket(window.innerWidth, window.innerHeight),
      sizeBucket(finding.boxWidthPx, finding.boxHeightPx),
    ].join(REPORT_KEY_SEPARATOR)
    if (!claimScrollBoxReport(this.options.reportStore, reportKey, Date.now())) return
    this.reportsThisPageLoad++
    this.options.report({
      finding,
      route,
      windowWidthPx: window.innerWidth,
      windowHeightPx: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio,
      checksSkippedBelowMinimumWindow: this.checksSkippedBelowMinimumWindow,
    })
  }
}

export function startScrollBoxWatcher(options: ScrollBoxWatcherOptions): () => void {
  const watcher = new ScrollBoxWatcher(options)
  watcher.start()
  return () => watcher.stop()
}
